<template>
  <div class="table-container">
    <md-table
      ref="queryManagementTable"
      v-model="rows"
      md-card
      md-fixed-header
      @md-selected="onSelect"
      v-if="0 < rows.length"
      class="table"
    >
      <md-table-row
        slot="md-table-row"
        slot-scope="{ item }"
        class="md-primary"
        md-selectable="single"
      >
        <md-table-cell
          v-for="(col, index) in columns"
          :key="index"
          :md-label="col.label"
          :style="col.style"
        >
          <!-- こちらのカスタムコンポーネントは呼び出し元でヘッダー定義の部分で用意する -->
          <component
            v-if="col.isCustom"
            :is="{ render: h => col.render(h, item) }"
          />
          <!-- カスタムコンポーネントが無く、アイコンでもない場合はテキストを表示 -->
          <span v-else-if="!col.isIcon">{{ item[col.data] }}</span>
          <!-- ステータスアイコンを表示する -->
          <span v-else>
            <div class="status-icon">
              <div
                v-if="getStatusConfig(item)"
                :gl-tooltip="getStatusConfig(item).tooltip"
                gl-tooltip-direction="right"
                :class="{
                  'requires-update-tooltip': item.status === 'RequiresUpdate',
                  'next-reservation-failed-tooltip':
                    item.status === 'NextReservationFailed',
                }"
              >
                <md-icon :class="item.status.toLowerCase()">
                  {{ getStatusConfig(item).icon }}
                </md-icon>
              </div>
            </div>
          </span>
        </md-table-cell>
      </md-table-row>
    </md-table>
    <div class="empty-container" v-if="rows.length === 0 && !loading">
      <md-empty-state
        md-icon="directions_run"
        md-label="さっそく作成しましょう！"
        md-description="まだデータが作成されていません。"
      >
      </md-empty-state>
    </div>
    <div v-if="loading" class="loading-backdrop">
      <spinner></spinner>
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/Spinner.vue'
import { getReservationStatus, getQueryStatus } from '@/const/statusConfig'
/**
 * md-tableのラッパーコンポーネント
 *
 */
export default {
  components: {
    Spinner,
  },
  props: {
    /**
     * タブ（履歴・予約）がどちらか
     * 履歴：history
     * 予約：reservation
     */
    dataType: {
      type: String,
      default: 'history',
    },
    /**
     * 出力する行データ
     */
    rows: {
      type: Array,
      default: () => [],
    },
    /**
     * 列モデル
     */
    columns: {
      type: Array,
      default: () => [],
    },
    /**
     * ロードステータス
     */
    loading: {
      type: Boolean,
      default: true,
    },
  },
  data: () => {
    return {}
  },
  methods: {
    /**
     * 一覧から選択
     */
    onSelect(selected) {
      this.$emit('row-selected', selected || null)
    },
    /**
     * テーブルの選択をクリア
     */
    clearSelection() {
      const table = this.$refs.queryManagementTable
      if (table && table.syncSelectedValue) {
        table.syncSelectedValue(null)
      }
    },
    /**
     * ステータス値からステータスオブジェクトを取得（予約用）
     */
    getReservationStatus,
    /**
     * ステータス値からステータスオブジェクトを取得（分析履歴用）
     */
    getQueryStatus,
    /**
     * 行が予約かどうかを判定
     */
    isReservation() {
      return this.dataType === 'reservation'
    },
    /**
     * ステータスに対応するオブジェクトを取得
     */
    getStatusConfig(item) {
      return this.isReservation()
        ? this.getReservationStatus(item.status)
        : this.getQueryStatus(item.status)
    },
  },
}
</script>

<style lang="scss" scoped>
/* ステータスアイコン色の定義 */
$completed: #00c752;
$working: #616161;
$noresult: #ffab00;
$error: #da4336;

/* テーブルコンテナ：全体の枠線と影 */
.table-container {
  height: 100%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.12) inset;
  border-radius: 2px;

  /* テーブル本体のレイアウト設定 */
  .table {
    table-layout: fixed;
    width: 100%;
    height: 100%;
  }
}

/* md-tableのセルパディング（Vue Material オーバーライド） */
/deep/ .md-table-cell-container {
  padding: 12px 16px 12px 12px !important;
}

/* md-tableのヘッダーラベルパディング（Vue Material オーバーライド） */
/deep/ .md-table-head-label {
  padding: 6px 16px 6px 12px !important;
  font-size: 13px;
}

/* md-table本体の影（Vue Material オーバーライド） */
/deep/ .md-table {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
  overflow: hidden !important;
}

/* md-tableコンテンツエリア（Vue Material オーバーライド） */
/deep/ .md-table-content {
  max-height: 100% !important;
  box-shadow: 0 16px 16px rgba(0, 0, 0, 0.15) !important;
}

/* ステータスアイコンコンテナとステータス別カラー */
.status-icon {
  /* 完了状態：緑色 */
  .completed {
    color: $completed;
  }

  /* エラー状態：赤色 */
  .error {
    color: $error;
  }

  /* 実行待ち状態：緑色 */
  .waiting {
    color: $completed;
  }

  /* 一時休止状態：黄色 */
  .paused {
    color: $noresult;
  }

  /* 要再更新状態：黄色（警告） */
  .requiresupdate,
  .nextreservationfailed {
    color: $noresult;
  }

  /* 実行中状態：グレー */
  .working,
  .running,
  .queued {
    color: $working;
  }
}

/* ローディング中の全画面オーバーレイ */
.loading-backdrop {
  z-index: 1000;
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(128, 128, 128, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
}
/deep/ .requires-update-tooltip::after {
  max-width: 400px !important;
  width: 360px !important;
  white-space: normal !important;
  word-wrap: break-word !important;
  padding: 6px 6px !important;
}
/deep/ .next-reservation-failed-tooltip::after {
  max-width: 400px !important;
  width: 360px !important;
  white-space: normal !important;
  word-wrap: break-word !important;
  padding: 6px 6px !important;
}
</style>
